import {
  GridRenderCellParams,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  DataGridProProps,
  type GridValidRowModel,
} from '@mui/x-data-grid-pro';
import { TFunction } from 'i18next';

import { statusTextBadgeTypography } from 'components/Search/components/fields/styles';
import { formatSubscriptionDuration } from 'screens/users/utility/subscriptionUtilities';
import { MM_DD_YY, MMM_DD_YYYY_C_12HHMM } from 'utility/constants';
import { formatCurrency, formatDate, hasValue, toTitleCase } from 'utility/functions';
import CheckCircle from '@mui/icons-material/CheckCircle';

// Generic components
import { IconAndStatusSwitcher } from './components/elements/IconAndStatusSwitcher';
import StatusIndicators from './components/fields/generic/StatusIndicators/StatusIndicators';
import SummaryCard from './components/fields/generic/SummaryCard';
import AuthorsField from './components/fields/generic/AuthorsField';
import TitleField from './components/fields/generic/TitleField';
import WorkflowField from './components/fields/generic/WorkflowField';
import { AssetTypeIconCell } from 'screens/editorial/asset/components/common/AssetTypeIconCell';
import { TitleCell } from 'screens/editorial/asset/components/common/TitleCell';
import { DateCell } from 'screens/editorial/asset/components/common/DateCell';

type GridInitialStatePro = DataGridProProps['initialState'];

// Editorial components
import EditorialSectionsField from './components/fields/editorial/EditorialSectionsField';
import DepthField from './components/fields/editorial/DepthField';
import { hasPrintPrivilege } from 'utility/privileges';

// Saved search components
import SearchSavedDefaultField from './components/fields/savedsearch/SearchSavedDefaultField';
import SearchSavedNameField from './components/fields/savedsearch/SearchSavedNameField';
import SearchSavedUserField from './components/fields/savedsearch/SearchSavedUserField';

// User components
import UserEmailField from './components/fields/user/UserEmailField';
import { StatusText } from 'screens/users/components/SubscribeCard/StatusText';

// Syndication components
import SyndicationCategoryField from './components/fields/syndication/SyndicationCategoryField';
import SyndicationDriverField from './components/fields/syndication/SyndicationDriverField';

// Service components
import { ServiceFlagsField } from './components/fields/services/ServiceFlagsField';
import { ServiceNameField } from './components/fields/services/ServiceNameField';

import {
  CampaignScheduleChip,
  CampaignStatusChip,
} from 'screens/community/emailreach/CampaignsSearch/components/CampaignStatusChip';

import {
  UserGridColDef,
  EditorialGridColDef,
  EditorialSavedSearchColDef,
  ServiceGridColDef,
  WireGridColDef,
  SearchContextType,
  BroadcastMessageColDef,
  BroadcastChannelColDef,
  BroadcastAccountColDef,
  ChannelGridColDef,
} from './types';
import { CollectionGridColDef } from 'screens/editorial/asset/components/collection/components/CollectionAssetEditorMain';
import { TSessionConfigData } from 'providers/contexts/CosmosContext';
import { ChannelOptionsType } from 'screens/syndication/views/search/syndication/WireSearchWithContext';
import {
  BroadcastMessageChannelsCell,
  BroadcastMessageTitleCell,
} from 'screens/notifier/Search/components/NotifierSearchComponents';
import { NotifierStatusBadge } from 'screens/notifier/Search/components/NotifierStatusBadge';
import {
  ChannelStatusBadge,
  ChannelDeliveryTargetsCell,
  ChannelRulesCountBadge,
} from 'screens/notifier/Channels/components/ChannelsSearchComponents';
import {
  ConnectionPlatformCell,
  ConnectionActionsCell,
} from 'screens/notifier/Connections/components/ConnectionsSearchComponents';
import {
  BroadcastMessageItem,
  BroadcastChannelItem,
  BroadcastAccountItem,
} from 'providers/contexts/extensions/Broadcast';
import { EditorialConfigData } from 'utility/types/dataProvider';
import { Stack } from '@mui/material';
import TemplatesRowActions from './components/fields/editorial/TemplatesRowActions';
import { SearchRowActionsCell } from './components/SearchRowActionsCell';
import {
  DASHBOARD_RESOURCE,
  DashboardGridColDef,
  getDashboardSearchColumns,
} from 'screens/dashboard/custom-dashboards/utils/constants';
import { SubscriptionIconHelper } from 'components/generic/SubscriptionIconHelper';

const dateFormatOptions: Intl.DateTimeFormatOptions = {
  hour12: true,
  hour: '2-digit',
  minute: '2-digit',
  day: '2-digit',
  month: 'short',
  year: 'numeric',
};

const fallbackGridInitState: GridInitialStatePro = {
  preferencePanel: {
    open: false,
  },
};

const editorialGridInitialState: GridInitialStatePro = {
  preferencePanel: {
    open: false,
  },
  columns: {
    columnVisibilityModel: {
      delete_time: false,
      slug: false,
      sections: false,
      update_time: false,
      priority: false,
      workflow_id: false,
      template: false,
    },
  },
};

const userGridInitialState: GridInitialStatePro = {
  preferencePanel: {
    open: false,
  },
  columns: {
    columnVisibilityModel: {
      uuid: false,
      department: false,
      title: false,
      screen_name: false,
    },
  },
};

const broadcastMessageGridInitialState: GridInitialStatePro = {
  preferencePanel: {
    open: false,
  },
  columns: {
    columnVisibilityModel: {
      title: true,
      send_date: true,
      send_status: true,
      target: true,
      type: true,
      channels: true,
      domain: false,
      asset_app: false,
    },
  },
};

const broadcastChannelGridInitialState: GridInitialStatePro = {
  preferencePanel: {
    open: false,
  },
  columns: {
    columnVisibilityModel: {
      name: true,
      disabled: true,
      delivery_targets: true,
      auto_notification_rules: true,
    },
  },
};

const broadcastAccountGridInitialState: GridInitialStatePro = {
  preferencePanel: {
    open: false,
  },
  columns: {
    columnVisibilityModel: {
      provider: true,
      user_name: true,
      actions: true,
    },
  },
};

const transactionsGridInitialState: GridInitialStatePro = {
  preferencePanel: {
    open: false,
  },
  columns: {
    columnVisibilityModel: {
      test: false,
    },
  },
};

export const getGridInitialState = (resource?: string): GridInitialStatePro => {
  switch (resource) {
    case 'editorial': {
      return editorialGridInitialState;
    }
    case 'user/account/multisite':
    case 'user': {
      return userGridInitialState;
    }
    case 'broadcast/message': {
      return broadcastMessageGridInitialState;
    }
    case 'broadcast/channel': {
      return broadcastChannelGridInitialState;
    }
    case 'broadcast/account': {
      return broadcastAccountGridInitialState;
    }
    case 'pay/transactions': {
      return transactionsGridInitialState;
    }
    default: {
      return fallbackGridInitState;
    }
  }
};

type GetGridColumnDefsParams = {
  t: TFunction;
  view: SearchContextType['view'];
  resource?: string;
  accountType?: string;
  subEnabled?: boolean;
  sessionConfig?: TSessionConfigData;
  activeSite?: string;
  // wire specific
  wireChannelOptions?: ChannelOptionsType[];
  hasMiniMenuEnabled?: boolean;
  showDomainColumn?: boolean;
  editorialConfig?: EditorialConfigData;
};

type GetGridColumnDefsReturnType =
  | UserGridColDef[]
  | EditorialGridColDef[]
  | EditorialSavedSearchColDef[]
  | ServiceGridColDef[]
  | WireGridColDef[]
  | BroadcastMessageColDef[]
  | BroadcastChannelColDef[]
  | BroadcastAccountColDef[]
  | ChannelGridColDef[]
  | DashboardGridColDef[]
  | CollectionGridColDef[];

const formatStatusLabel = (value?: string) => {
  if (!value) {
    return '—';
  }
  return toTitleCase(value.replace(/_/g, ' '));
};

export const getGridColumnDefs = ({
  t,
  view,
  resource,
  accountType,
  subEnabled,
  sessionConfig,
  wireChannelOptions,
  hasMiniMenuEnabled = false,
  showDomainColumn = false,
  editorialConfig,
  activeSite,
}: GetGridColumnDefsParams): GetGridColumnDefsReturnType => {
  const timeZone = sessionConfig?.time_zone;

  switch (resource) {
    case 'editorial': {
      const editorialCols: EditorialGridColDef[] = getEditorialColumnDefs({
        t,
        view,
        resource,
        sessionConfig,
        editorialConfig,
      });
      return editorialCols;
    }
    case 'editorial/templates': {
      const templateCols: EditorialGridColDef[] = getTemplatesColDefs({
        t: t,
        view: view,
        timeZone: timeZone,
      });
      return templateCols;
    }
    case 'broadcast/message': {
      const broadcastColDefs = getBroadcastMessageColDefs({ t, timeZone });
      return broadcastColDefs;
    }
    case 'broadcast/channel': {
      const channelColDefs = getBroadcastChannelColDefs({ t, timeZone });
      return channelColDefs;
    }
    case 'broadcast/account': {
      const accountColDefs = getBroadcastAccountColDefs({ t });
      return accountColDefs;
    }
    // Editorial search saved
    case 'savedsearch': {
      const baseColumns: EditorialSavedSearchColDef[] = [
        {
          field: 'name',
          headerName: t('blox.general.name'),
          renderCell: (params: GridRenderCellParams) => (
            <SearchSavedNameField record={params.row} />
          ),
          flex: 4,
          hideable: false,
        },
        {
          field: 'user_id',
          headerName: t('blox.search.table.owner', 'Owner'),
          flex: 2,
          renderCell: (params: GridRenderCellParams) => (
            <SearchSavedUserField record={params.row} />
          ),
        },
        {
          field: 'default',
          headerName: t('blox.search.table.default', 'Default'),
          flex: 2,
          renderCell: (params: GridRenderCellParams) => (
            <SearchSavedDefaultField record={params.row} />
          ),
        },
        ...(hasMiniMenuEnabled
          ? [
              {
                field: 'actions',
                type: 'actions' as const, // prevents widening to `string`
                width: 50,
                sortable: false,
                resizable: false,
                disableColumnMenu: true,
                hideable: false,
                renderCell: () => <SearchRowActionsCell />,
              },
            ]
          : []),
      ];

      return baseColumns;
    }
    case 'wire/channel/search': {
      // Find the item in channelOptions with
      // type sort and return its options
      const sortOptionsItem = wireChannelOptions?.find(
        (option: any) => option.type === 'sort',
      );
      // Ensure sortOptions is an array; default to
      // an empty array if no 'sort' item found or if it's not an array
      const sortOptions = Array.isArray(sortOptionsItem?.options)
        ? sortOptionsItem.options
        : [];

      // base wire column definitions
      const wireColDefs: WireGridColDef[] = [
        {
          field: 'title',
          headerName: t('blox.general.title'),
          renderCell: (params: GridRenderCellParams) =>
            view === 'list' ? (
              <TitleField record={params?.row} />
            ) : (
              <SummaryCard
                record={params?.row}
                view={view}
              />
            ),
          flex: 2,
          minWidth: view === 'list' ? 250 : 500,
          sortable: !!sortOptions?.find(
            (item: { label: string }) => 'title' === item.label.trim().toLowerCase(),
          ),
          hideable: false,
        },
        {
          field: 'categories',
          headerName: t('blox.search.table.categories', 'Categories'),
          renderCell: (params: GridRenderCellParams) => (
            <SyndicationCategoryField record={params.row} />
          ),
          minWidth: 150,
          flex: 0.5,
          sortable: !!sortOptions?.find(
            (item: { label: string }) => 'categories' === item.label.trim().toLowerCase(),
          ),
        },
        {
          field: 'start_time',
          headerName: t('blox.editorial.asset.start_time.label'),
          valueGetter: (value: any) => {
            if (!value) return '';
            const formattedDate = formatDate({
              date: value,
              shiftTz: timeZone,
              format: dateFormatOptions,
            });
            return formattedDate;
          },
          minWidth: 150,
          flex: 0.5,
          sortable: !!sortOptions?.find(
            (item: { label: string }) => 'start_time' === item.label.trim().toLowerCase(),
          ),
        },
        {
          field: 'slug',
          headerName: t('blox.editorial.metadata.group.print_slug.label'),
          minWidth: 150,
          flex: 0.5,
          sortable: !!sortOptions?.find(
            (item: { label: string }) => 'slug' === item.label.trim().toLowerCase(),
          ),
        },
        {
          field: 'status',
          headerName: t('blox.general.status'),
          renderCell: (params: GridRenderCellParams) => (
            <IconAndStatusSwitcher
              record={params?.row}
              resource={resource}
            />
          ),
          width: 100,
          sortable: !!sortOptions?.find(
            (item: { label: string }) => 'status' === item.label.trim().toLowerCase(),
          ),
          resizable: false,
        },
      ];
      // Render actions only when the shared mini-menu gate says the menu can mount.
      if (hasMiniMenuEnabled) {
        wireColDefs.push({
          field: 'actions',
          type: 'actions',
          width: 50,
          sortable: false,
          resizable: false,
          disableColumnMenu: true,
          hideable: false,
          renderCell: () => <SearchRowActionsCell />,
        });
      }
      return wireColDefs;
    }
    case 'wire/channel': {
      const baseColumns: ChannelGridColDef[] = [
        {
          field: 'driver',
          headerName: 'Driver',
          flex: 1,
          renderCell: (params: any) => {
            return <SyndicationDriverField record={params.row} />;
          },
        },
        {
          field: 'name',
          headerName: 'Name',
          flex: 1,
        },
      ];

      if (hasMiniMenuEnabled) {
        baseColumns.push({
          field: 'actions',
          type: 'actions',
          width: 50,
          sortable: false,
          resizable: false,
          disableColumnMenu: true,
          hideable: false,
          renderCell: () => <SearchRowActionsCell />,
        });
      }

      return baseColumns;
    }
    case 'user/account/multisite':
    case 'user': {
      const addSubsCols = subEnabled && accountType === 'normal';
      const usersColDefs: UserGridColDef[] = getUserBaseColDefs({
        t,
        timeZone,
        showDomainColumn,
      });
      if (addSubsCols) {
        const subsCols = getUserSubscriptionColDefs({ timeZone, t });
        usersColDefs.push(...subsCols);
      }
      return usersColDefs;
    }
    case 'subscription/services': {
      const serviceColDefs: ServiceGridColDef[] = getServiceBaseColDefs({ t });
      return serviceColDefs;
    }
    case 'pay/transactions': {
      const formatTransactionDate = (value?: string) => {
        if (!value) {
          return '';
        }
        return formatDate({
          date: value,
          shiftTz: timeZone,
          format: dateFormatOptions,
        });
      };

      return [
        {
          field: 'created_at',
          headerName: t('blox.transactions.table.date', { defaultValue: 'Date / Time' }),
          flex: 1.2,
          minWidth: 180,
          sortable: true,
          hideable: false,
          valueGetter: ({ row }: GridRenderCellParams) => row?.created_at || '',
          renderCell: ({ row }: GridRenderCellParams) =>
            formatTransactionDate(row?.created_at),
        },
        {
          field: 'customer',
          headerName: t('blox.transactions.table.customer', {
            defaultValue: 'Customer Name',
          }),
          flex: 1,
          minWidth: 160,
          sortable: false,
        },
        {
          field: 'type',
          headerName: t('blox.transactions.table.type', { defaultValue: 'Type' }),
          flex: 0.8,
          minWidth: 140,
          sortable: false,
          valueGetter: ({ row }: GridRenderCellParams) => row?.type || '',
          renderCell: ({ row }: GridRenderCellParams) => row?.type || '',
        },
        {
          field: 'amount',
          headerName: t('blox.transactions.table.amount', { defaultValue: 'Amount' }),
          flex: 0.6,
          minWidth: 120,
          sortable: true,
          valueGetter: ({ value }: GridRenderCellParams) => {
            if (value === null || value === undefined) {
              return value;
            }
            if (typeof value === 'number') {
              return value;
            }
            const parsed = Number(value);
            return Number.isNaN(parsed) ? value : parsed;
          },
          renderCell: (params: GridRenderCellParams) => {
            const raw =
              typeof params.row?.amount === 'number'
                ? params.row.amount
                : Number(params.row?.amount);
            if (typeof raw !== 'number' || Number.isNaN(raw)) {
              return params.value ?? '';
            }
            return (
              formatCurrency(raw, params.row?.currency || 'USD') ?? params.value ?? ''
            );
          },
        },
        {
          field: 'state',
          headerName: t('blox.transactions.table.state', { defaultValue: 'Status' }),
          flex: 0.7,
          minWidth: 140,
          sortable: false,
          valueGetter: ({ row }: GridRenderCellParams) => row?.state || '',
          renderCell: (params: GridRenderCellParams) => (
            <StatusText
              status={params?.row?.state}
              text={formatStatusLabel(params?.row?.state)}
              sx={statusTextBadgeTypography}
            />
          ),
        },
        {
          field: 'payment_handler',
          headerName: t('blox.transactions.table.paymentHandler', {
            defaultValue: 'Payment Handler',
          }),
          flex: 0.8,
          minWidth: 140,
          sortable: false,
        },
        {
          field: 'email',
          headerName: t('blox.transactions.table.email', { defaultValue: 'Email' }),
          flex: 1,
          minWidth: 180,
          sortable: false,
        },
        {
          field: 'app',
          headerName: t('blox.transactions.table.application', {
            defaultValue: 'Application',
          }),
          flex: 0.8,
          minWidth: 140,
          sortable: false,
          valueGetter: ({ row }: GridRenderCellParams) => row?.app || '',
          renderCell: ({ row }: GridRenderCellParams) => row?.app || '',
        },
        {
          field: 'test',
          headerName: t('blox.transactions.table.test', { defaultValue: 'Test' }),
          flex: 0.5,
          minWidth: 80,
          sortable: false,
          type: 'boolean',
          renderCell: (params: GridRenderCellParams) => {
            return params?.row?.test
              ? t('blox.editorial.infobar.yes')
              : t('blox.editorial.infobar.no');
          },
        },
      ];
    }
    case 'emailreach/account': {
      return [
        {
          field: 'name',
          headerName: t('blox.email_reach.account.table.name', 'Name'),
          sortable: false,
          resizable: true,
          flex: 2,
          type: 'string',
          minWidth: 150,
          hideable: false,
        },
        {
          field: 'vendor_name',
          headerName: t('blox.email_reach.account.table.provider', 'Email Provider'),
          sortable: false,
          resizable: true,
          flex: 1,
          type: 'string',
          minWidth: 150,
        },
        {
          field: 'is_default',
          headerName: t('blox.email_reach.account.table.default', 'Default'),
          sortable: false,
          resizable: true,
          flex: 1,
          type: 'boolean',
          minWidth: 100,
          renderCell: (params: GridRenderCellParams) => {
            return params?.row?.is_default ? (
              <span data-private>
                <CheckCircle
                  style={{ color: 'green' }}
                  fontSize="small"
                />
              </span>
            ) : (
              <span data-private></span>
            );
          },
        },
      ];
    }
    case 'emailreach/contact': {
      return [
        {
          field: 'email_address',
          headerName: t('blox.email_reach.contact.table.email', 'E-mail'),
          sortable: false,
          resizable: true,
          flex: 2,
          display: 'text',
          type: 'string',
          minWidth: 150,
          hideable: false,
        },
        {
          field: 'first_name',
          headerName: t('blox.email_reach.contact.table.first_name', 'First Name'),
          sortable: false,
          resizable: true,
          flex: 1,
          display: 'text',
          type: 'string',
          minWidth: 100,
        },
        {
          field: 'last_name',
          headerName: t('blox.email_reach.contact.table.last_name', 'Last Name'),
          sortable: false,
          resizable: true,
          flex: 1,
          display: 'text',
          type: 'string',
          minWidth: 100,
        },
        {
          field: 'status',
          headerName: t('blox.email_reach.common.status.label', 'Subscription Status'),
          sortable: false,
          resizable: true,
          flex: 1,
          display: 'text',
          type: 'string',
          minWidth: 100,
          renderCell: (params: GridRenderCellParams) =>
            formatStatusLabel(params?.row?.status),
        },
      ];
    }
    case 'emailreach/contactlist': {
      return [
        {
          field: 'name',
          headerName: t('blox.email_reach.contact_list.table.name', 'Title'),
          sortable: false,
          resizable: true,
          flex: 1,
          display: 'text',
          type: 'string',
          minWidth: 150,
          hideable: false,
        },
        {
          field: 'contact_count',
          headerName: t('blox.email_reach.contact_list.table.contacts', 'Contact Count'),
          sortable: false,
          resizable: true,
          flex: 1,
          display: 'text',
          type: 'number',
          minWidth: 100,
        },
      ];
    }
    case 'emailreach/campaign': {
      return [
        {
          field: 'title',
          headerName: t('blox.email_reach.campaign.table.title', 'Title'),
          sortable: false,
          resizable: true,
          flex: 2,
          type: 'string',
          minWidth: 150,
          hideable: false,
        },
        {
          field: 'email_subject',
          headerName: t('blox.email_reach.campaign.table.subject', 'Email Subject'),
          sortable: false,
          resizable: true,
          flex: 2,
          type: 'string',
          minWidth: 150,
        },
        {
          field: 'active',
          headerName: t('blox.email_reach.campaign.table.active', 'Active'),
          sortable: false,
          resizable: true,
          flex: 1,
          type: 'string',
          minWidth: 140,
          // A campaign is "Active" when it is not disabled. (The row's `active`
          // field is a different concept: whether the schedule has reached its
          // end date.)
          valueGetter: (value: any, row: GridValidRowModel) =>
            row?.disabled === true ? 'disabled' : 'active',
          renderCell: ({ row }: GridRenderCellParams) => (
            <CampaignStatusChip active={row?.disabled !== true} />
          ),
        },
        {
          field: 'frequency',
          headerName: t('blox.email_reach.campaign.table.schedule', 'Schedule'),
          sortable: false,
          resizable: true,
          flex: 1,
          type: 'string',
          minWidth: 140,
          // Should use GridValueGetter type.
          valueGetter: (value: any, row: GridValidRowModel) =>
            typeof row?.frequency === 'string' ? row.frequency : '',
          renderCell: ({ row }: GridRenderCellParams) => (
            <CampaignScheduleChip frequency={row?.frequency} />
          ),
        },
        ...(hasMiniMenuEnabled
          ? [
              {
                field: 'actions',
                type: 'actions' as const,
                width: 50,
                sortable: false,
                resizable: false,
                disableColumnMenu: true,
                hideable: false,
                renderCell: () => <SearchRowActionsCell />,
              },
            ]
          : []),
      ];
    }
    case 'emailreach/messages': {
      return [
        {
          field: 'title',
          headerName: t('blox.general.title'),
          sortable: false,
          resizable: true,
          flex: 2,
          display: 'text',
          type: 'string',
          minWidth: 150,
          hideable: false,
        },
        {
          field: 'status',
          headerName: t('blox.general.status'),
          sortable: false,
          resizable: true,
          flex: 1,
          display: 'text',
          type: 'string',
          minWidth: 100,
          valueFormatter: ({ value }: { value: string | undefined }) =>
            typeof value === 'string' ? toTitleCase(value) : value,
        },
        {
          field: 'sent_date',
          headerName: t('blox.email_reach.messages.table.sent_date', 'Sent Date'),
          sortable: false,
          resizable: true,
          flex: 1,
          display: 'text',
          type: 'date',
          minWidth: 100,
          valueGetter: (value: any) => {
            if (!value) return null;
            return value instanceof Date ? value : new Date(value);
          },
        },
      ];
    }
    case 'collections': {
      const columns = [
        {
          field: 'type',
          headerName: t('blox.editorial.asset.type.label'),
          renderCell: (params: GridRenderCellParams) => {
            return <AssetTypeIconCell type={params?.row.type} />;
          },
          flex: 1,
          hideable: false,
        },
        {
          field: 'title',
          headerName: t('blox.general.title'),
          renderCell: (params: GridRenderCellParams) => {
            return <TitleCell relatedAsset={params?.row} />;
          },
          flex: 6,
          hideable: false,
        },
        {
          field: 'start_time',
          headerName: t('blox.editorial.asset.start_time.alt_label'),
          renderCell: (params: GridRenderCellParams) => {
            return <DateCell date={params?.row.start_time} />;
          },
          flex: 1.5,
        },
        {
          field: 'status',
          headerName: t('blox.general.status'),
          renderCell: (params: GridRenderCellParams) => (
            <IconAndStatusSwitcher
              record={params?.row}
              resource={'collections'}
            />
          ),
          flex: 3,
          sortable: false,
        },
      ];
      return columns;
    }

    // Dashboards.
    case DASHBOARD_RESOURCE: {
      const dashboardCols = getDashboardSearchColumns(t);
      return dashboardCols;
    }

    /**
     * No resource match, return empty array.
     */
    default: {
      return [];
    }
  }
};

type GetEditorialColumnDefs = {
  t: TFunction;
  view: SearchContextType['view'];
  resource: string;
  sessionConfig?: TSessionConfigData;
  editorialConfig?: EditorialConfigData;
};

export const getEditorialColumnDefs = ({
  t,
  view,
  resource,
  sessionConfig,
  editorialConfig,
}: GetEditorialColumnDefs): EditorialGridColDef[] => {
  const timeZone = sessionConfig?.time_zone;

  // Print column conditions
  const hasPrint = hasPrintPrivilege(sessionConfig);
  const isPrintDepthEnabled = Boolean(
    editorialConfig?.asset_config?.article?.printDepthFactor && hasPrint,
  );

  const columns: EditorialGridColDef[] = [
    {
      ...GRID_CHECKBOX_SELECTION_COL_DEF,
      renderHeader: () => null,
      disableReorder: true,
      hideable: false,
      sortable: false,
    },
    {
      field: 'title',
      headerName: t('blox.general.title'),
      renderCell: (params: GridRenderCellParams) =>
        view === 'list' ? (
          <TitleField record={params?.row} />
        ) : (
          <SummaryCard
            record={params?.row}
            view={view}
          />
        ),
      flex: 2,
      minWidth: view === 'list' ? 250 : 450,
      hideable: false,
    },
    {
      field: 'sections',
      headerName: t('blox.editorial.asset.sections.label'),
      renderCell: (params: GridRenderCellParams) => (
        <EditorialSectionsField record={params.row} />
      ),
      flex: 0.5,
      minWidth: 150,
      sortable: false,
    },
    {
      field: 'update_time',
      headerName: t('blox.editorial.asset.update_time.label'),
      valueGetter: (value: any) => {
        if (!value) return '';
        const formattedDate = formatDate({
          date: value,
          shiftTz: timeZone,
          format: dateFormatOptions,
        });
        return formattedDate;
      },
      flex: 0.75,
      minWidth: 150,
    },
    {
      field: 'authors',
      headerName: t('blox.editorial.asset.authors.label_one'),
      renderCell: (params: GridRenderCellParams) => (
        <AuthorsField
          record={params.row}
          avatarMode={view === 'summary'}
        />
      ),
      flex: 1,
      minWidth: 150,
      sortable: false,
    },
    {
      field: 'start_time',
      headerName: t('blox.editorial.asset.start_time.label'),
      valueGetter: (value: any) => {
        if (!value) return '';
        const formattedDate = formatDate({
          date: value,
          shiftTz: timeZone,
          format: dateFormatOptions,
        });
        return formattedDate;
      },
      flex: 0.75,
      minWidth: 150,
    },
    {
      field: 'priority',
      headerName: t('blox.editorial.asset.properties.priority.label'),
      flex: 0.5,
      minWidth: 150,
    },
    {
      field: 'photo_sales',
      headerName: t('blox.editorial.asset.properties.photo_sales.label'),
      flex: 0.5,
      minWidth: 150,
      sortable: false,
    },
    {
      field: 'status',
      headerName: t('blox.general.status_primary'),
      renderCell: (params: GridRenderCellParams) => (
        <IconAndStatusSwitcher
          record={params?.row}
          resource={resource}
        />
      ),
      flex: 0.5,
      minWidth: 150,
      sortable: false,
    },
    {
      // A combination of fields as status indicators
      field: 'status_indicators',
      headerName: t('blox.general.status'),
      renderCell: (params: GridRenderCellParams) => (
        <StatusIndicators record={params?.row} />
      ),
      flex: 0.5,
      minWidth: 150,
      sortable: false,
    },
    {
      field: 'delete_time',
      headerName: t('blox.editorial.asset.delete_time.label'),
      valueGetter: (value: any) => {
        if (!value) return '';
        const formattedDate = formatDate({
          date: value,
          shiftTz: timeZone,
          format: dateFormatOptions,
        });
        return formattedDate;
      },
      flex: 0.75,
      minWidth: 150,
    },
    {
      field: 'slug',
      headerName: t('blox.editorial.metadata.group.print_slug.label'),
      flex: 0.5,
      minWidth: 150,
    },
    ...(hasPrint
      ? [
          {
            field: 'print_ready',
            headerName: t('blox.editorial.metadata.group.print_ready.label'),
            flex: 0.5,
            minWidth: 150,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => {
              const value = params.row?.print_ready;
              return value
                ? t('blox.editorial.metadata.group.print.ready.label')
                : t('blox.editorial.metadata.group.print.not_ready.label');
            },
          },
        ]
      : []),
    ...(isPrintDepthEnabled
      ? [
          {
            field: 'depth',
            headerName: t('blox.editorial.metadata.group.print_depth.label'),
            flex: 0.4,
            minWidth: 100,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => (
              <DepthField record={params.row} />
            ),
          },
        ]
      : []),
    {
      field: 'workflow_id',
      headerName: t('blox.editorial.asset.properties.workflow.label'),
      renderCell: (params: GridRenderCellParams) => <WorkflowField record={params.row} />,
      flex: 1,
      minWidth: 150,
      sortable: false,
    },
    {
      field: 'last_modified_by',
      headerName: t('blox.search.table.last_modified_by', 'Last Modified By'),
      renderCell: (params: GridRenderCellParams) =>
        params.row?.last_modified_by?.email.trim() || '',
      flex: 1,
      minWidth: 150,
      sortable: false,
    },
    {
      field: 'template',
      headerName: t('blox.general.template'),
      renderCell: (params: GridRenderCellParams) =>
        params.row?.template ? t('blox.general.yes') : t('blox.general.no'),
      flex: 0.5,
      minWidth: 100,
      sortable: false,
    },
    {
      field: 'actions',
      type: 'actions',
      width: 50,
      sortable: false,
      resizable: false,
      disableColumnMenu: true,
      hideable: false,
      renderCell: () => <SearchRowActionsCell />,
    },
  ];
  return columns;
};

type GetUserBaseColDefsArgs = {
  timeZone?: string;
  showDomainColumn?: boolean;
  t: TFunction;
};

export const getUserBaseColDefs = ({
  timeZone,
  showDomainColumn = false,
  t,
}: GetUserBaseColDefsArgs): UserGridColDef[] => {
  const cols: UserGridColDef[] = [
    {
      field: 'uuid',
      headerName: t('blox.search.table.uuid', 'UUID'),
      sortable: true,
      resizable: true,
      flex: 1,
      display: 'text',
      type: 'string',
      minWidth: 100,
    },
    {
      field: 'email',
      headerName: t('resources.user.fields.email'),
      sortable: true,
      resizable: true,
      flex: 1,
      display: 'flex',
      type: 'string',
      minWidth: 100,
      hideable: false,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <UserEmailField
            data={params.row}
            external={showDomainColumn}
          />
        );
      },
    },
    {
      field: 'first_name',
      headerName: t('resources.user.fields.first_name'),
      sortable: true,
      resizable: true,
      flex: 1,
      display: 'text',
      type: 'string',
      minWidth: 100,
      renderCell: (params: GridRenderCellParams) => {
        const firstName = hasValue(params?.row?.first_name)
          ? params?.row?.first_name
          : null;
        if (firstName === null) return null;
        return <span data-private>{firstName}</span>;
      },
    },
    {
      field: 'last_name',
      headerName: t('resources.user.fields.last_name'),
      sortable: true,
      resizable: true,
      flex: 1,
      display: 'text',
      type: 'string',
      minWidth: 100,
      renderCell: (params: GridRenderCellParams) => {
        const lastName = hasValue(params?.row?.last_name) ? params?.row?.last_name : null;
        if (lastName === null) return null;
        return <span data-private>{lastName}</span>;
      },
    },
    {
      field: 'screen_name',
      headerName: t('resources.user.fields.screen_name'),
      sortable: true,
      resizable: true,
      flex: 1,
      display: 'text',
      type: 'string',
      minWidth: 100,
      renderCell: (params: GridRenderCellParams) => {
        const screenName = hasValue(params?.row?.screen_name)
          ? params?.row?.screen_name
          : null;
        if (screenName === null) return null;
        return <span data-private>{screenName}</span>;
      },
    },
    {
      field: 'department',
      headerName: t('resources.user.fields.department'),
      sortable: false, // Backend does not support sorting on this field
      resizable: true,
      flex: 1,
      display: 'text',
      type: 'string',
      minWidth: 100,
      renderCell: (params: GridRenderCellParams) => {
        const dept = hasValue(params?.row?.department) ? params?.row?.department : null;
        if (dept === null) return null;
        return <span data-private>{dept}</span>;
      },
    },
    {
      field: 'title',
      headerName: t('resources.user.fields.title'),
      sortable: true,
      resizable: true,
      flex: 1,
      display: 'text',
      type: 'string',
      minWidth: 100,
      renderCell: (params: GridRenderCellParams) => {
        const title = hasValue(params?.row?.title) ? params?.row?.title : null;
        if (title === null) return null;
        return <span data-private>{title}</span>;
      },
    },
    {
      field: 'create_time',
      headerName: t('blox.search.table.created_date', 'Created Date'),
      sortable: true,
      resizable: true,
      flex: 1,
      display: 'text',
      type: 'date',
      minWidth: 100,
      valueFormatter: (value, row) => {
        const startDate = row?.create_time;
        const result = hasValue(startDate)
          ? formatDate({
              date: startDate,
              shiftTz: timeZone,
              format: MM_DD_YY,
            })
          : '';
        return result;
      },
    },
  ];

  if (showDomainColumn) {
    cols.push({
      field: 'domain',
      headerName: t('blox.search.table.domain', 'Domain'),
      width: 240,
      sortable: false,
    });
  }
  return cols;
};

type GetUserSubscriptionColDefsArgs = {
  timeZone?: string;
  t: TFunction;
};

export const getUserSubscriptionColDefs = ({
  timeZone,
  t,
}: GetUserSubscriptionColDefsArgs): UserGridColDef[] => {
  const userSubColDefs: UserGridColDef[] = [
    {
      field: 'subscription.state',
      headerName: t('blox.search.table.subscription_state', 'Subscription State'),
      sortable: false,
      resizable: true,
      flex: 1,
      display: 'flex',
      type: 'string',
      minWidth: 100,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <StatusText
            status={params?.row?.subscription?.state}
            text={params?.row?.subscription?.state || 'N/A'}
            sx={statusTextBadgeTypography}
          />
        );
      },
    },
    {
      field: 'subscription.rate_price',
      headerName: t('blox.subscription.rate.label'),
      sortable: false,
      resizable: true,
      flex: 1,
      display: 'text',
      type: 'string',
      minWidth: 100,
      valueFormatter: (value, row) => {
        const ratePrice = row?.subscription?.rate_price;
        const result = hasValue(ratePrice) ? formatCurrency(ratePrice) : '';
        return result;
      },
    },
    {
      field: 'subscription.rate_duration',
      headerName: t('blox.search.table.term', 'Term'),
      sortable: false,
      resizable: true,
      flex: 1,
      display: 'text',
      type: 'string',
      minWidth: 100,
      valueFormatter: (value, row) => {
        const rate_duration = row?.subscription?.rate_duration;
        const result = hasValue(rate_duration)
          ? formatSubscriptionDuration(rate_duration)
          : '';
        return result;
      },
    },
    {
      field: 'subscription.service_name',
      headerName: t('blox.search.table.services', 'Services'),
      sortable: false,
      resizable: true,
      flex: 1,
      display: 'text',
      type: 'string',
      minWidth: 100,
      valueFormatter: (value, row) => {
        const servicesName = row?.subscription?.service_name;
        return hasValue(servicesName) ? servicesName : '';
      },
    },
    {
      field: 'subscription.rate_name',
      headerName: t('blox.search.table.rate_name', 'Rate Name'),
      sortable: false,
      resizable: true,
      flex: 1,
      display: 'text',
      type: 'string',
      minWidth: 100,
      valueFormatter: (value, row) => {
        const rateName = row?.subscription?.rate_name;
        return hasValue(rateName) ? rateName : '';
      },
    },
    {
      field: 'subscription.expires',
      headerName: t('blox.search.table.term_expires', 'Term Expires'),
      sortable: false,
      resizable: true,
      flex: 1,
      display: 'text',
      type: 'date',
      minWidth: 100,
      valueFormatter: (value, row) => {
        const endDate = row?.subscription?.expires;
        const result = hasValue(endDate)
          ? formatDate({
              date: endDate,
              shiftTz: timeZone,
              format: MM_DD_YY,
            })
          : '';
        return result;
      },
    },
  ];
  return userSubColDefs;
};

type GetServiceBaseColDefsArgs = {
  t: TFunction;
};

export const getServiceBaseColDefs = ({
  t,
}: GetServiceBaseColDefsArgs): ServiceGridColDef[] => {
  const serviceBaseColDefs: ServiceGridColDef[] = [
    {
      field: 'type_icon',
      headerName: t('blox.search.table.service_icon', 'Service Icon'),
      renderHeader: () => <></>,
      sortable: false,
      resizable: true,
      flex: 0,
      width: 50,
      renderCell: (params) => {
        const type = params?.row?.type;
        return <SubscriptionIconHelper iconType={type} />;
      },
    },
    {
      field: 'name',
      headerName: t('blox.search.table.service_title', 'Service Title'),
      sortable: true,
      resizable: true,
      flex: 2,
      minWidth: 150,
      display: 'text',
      type: 'string',
      hideable: false,
      renderCell: (params) => {
        const name = params?.row?.name || '';
        const isDisabled = Boolean(params?.row?.disabled);
        return (
          <ServiceNameField
            name={name}
            isDisabled={isDisabled}
          />
        );
      },
    },
    {
      field: 'flags',
      headerName: t('blox.editorial.asset.flags.label'),
      sortable: false,
      resizable: true,
      flex: 1,
      minWidth: 150,
      display: 'text',
      type: 'string',
      renderCell: (params) => {
        const isDisabled = Boolean(params?.row?.disabled);
        const isHidden = Boolean(params?.row?.hidden);
        const isTestmode = Boolean(params?.row?.testmode);
        return (
          <ServiceFlagsField
            isDisabled={isDisabled}
            isHidden={isHidden}
            isTestmode={isTestmode}
          />
        );
      },
    },
    {
      field: 'type',
      headerName: t('blox.search.table.service_type', 'Service Type'),
      sortable: true,
      resizable: true,
      flex: 1,
      minWidth: 100,
      display: 'text',
      type: 'string',
    },
    {
      field: 'active_users',
      headerName: t('blox.search.table.active_subscribers', 'Active Subscribers'),
      sortable: true,
      resizable: true,
      flex: 1,
      minWidth: 60,
      display: 'text',
      type: 'number',
    },
    {
      field: 'new_users',
      headerName: t('blox.search.table.new_subscribers', 'New Subscribers'),
      sortable: true,
      resizable: true,
      flex: 1,
      minWidth: 60,
      display: 'text',
      type: 'number',
    },
    {
      field: 'stopped_users',
      headerName: t('blox.search.table.stopped_subscribers', 'Stopped Subscribers'),
      sortable: true,
      resizable: true,
      flex: 1,
      minWidth: 60,
      display: 'text',
      type: 'number',
    },
    {
      field: 'payment_handler',
      headerName: t('blox.search.table.payment_handler', 'Payment Handler'),
      sortable: false,
      resizable: true,
      flex: 1,
      minWidth: 100,
      display: 'text',
      type: 'string',
    },
    {
      field: 'id',
      headerName: t('blox.search.table.service_id', 'Service ID'),
      sortable: false,
      resizable: true,
      flex: 1,
      minWidth: 50,
      display: 'text',
      type: 'number',
    },
  ];

  return serviceBaseColDefs;
};

type GetBroadcastChannelColDefsArgs = {
  t: TFunction;
  timeZone?: string;
};

export const getBroadcastChannelColDefs = ({
  t,
  timeZone,
}: GetBroadcastChannelColDefsArgs): BroadcastChannelColDef[] => {
  const coldefs: BroadcastChannelColDef[] = [
    {
      field: 'name',
      headerName: t('blox.notifier.channel_grid.name'),
      sortable: true,
      resizable: true,
      flex: 2,
      display: 'flex',
      type: 'string',
      minWidth: 200,
      hideable: false,
      renderCell: (params: GridRenderCellParams<BroadcastChannelItem>) => {
        const channel = params?.row;
        if (!channel) return null;
        return (
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
              width: '100%',
            }}
          >
            <span style={{ fontWeight: 500 }}>{channel.name}</span>
          </Stack>
        );
      },
    },
    {
      field: 'disabled',
      headerName: t('blox.general.status'),
      sortable: false,
      resizable: true,
      flex: 1,
      display: 'flex',
      type: 'string',
      minWidth: 120,
      renderCell: (params: GridRenderCellParams<BroadcastChannelItem>) => {
        const channel = params?.row;
        if (!channel) return null;
        return <ChannelStatusBadge channel={channel} />;
      },
    },
    {
      field: 'delivery_targets',
      headerName: t('blox.notifier.channel_grid.delivery_targets'),
      sortable: false,
      resizable: true,
      flex: 2,
      display: 'flex',
      type: 'string',
      minWidth: 220,
      renderCell: (params: GridRenderCellParams<BroadcastChannelItem>) => {
        const channel = params?.row;
        if (!channel) return null;
        return <ChannelDeliveryTargetsCell channel={channel} />;
      },
    },
    {
      field: 'auto_notification_rules',
      headerName: t('blox.notifier.channel_grid.auto_notification_rules'),
      sortable: false,
      resizable: true,
      flex: 1,
      display: 'flex',
      type: 'number',
      minWidth: 140,
      renderCell: (params: GridRenderCellParams<BroadcastChannelItem>) => {
        const channel = params?.row;
        if (!channel) return null;
        return <ChannelRulesCountBadge channel={channel} />;
      },
    },
  ];

  return coldefs;
};

type GetBroadcastAccountColDefsArgs = {
  t: TFunction;
};

export const getBroadcastAccountColDefs = ({
  t,
}: GetBroadcastAccountColDefsArgs): BroadcastAccountColDef[] => {
  const coldefs: BroadcastAccountColDef[] = [
    {
      field: 'provider',
      headerName: t('blox.notifier.settings.social_accounts.platform'),
      sortable: false,
      resizable: true,
      flex: 1,
      display: 'flex',
      type: 'string',
      minWidth: 150,
      hideable: false,
      renderCell: (params: GridRenderCellParams<BroadcastAccountItem>) => {
        const account = params?.row;
        if (!account) return null;
        return <ConnectionPlatformCell account={account} />;
      },
    },
    {
      field: 'user_name',
      headerName: t('blox.notifier.settings.social_accounts.account_name'),
      sortable: false,
      resizable: true,
      flex: 2,
      display: 'flex',
      type: 'string',
      minWidth: 200,
      renderCell: (params: GridRenderCellParams<BroadcastAccountItem>) => {
        const account = params?.row;
        if (!account) return null;
        return <span>{account.user_name ?? '—'}</span>;
      },
    },
    {
      field: 'actions',
      headerName: t('blox.notifier.settings.social_accounts.actions'),
      sortable: false,
      resizable: false,
      flex: 0.5,
      display: 'flex',
      type: 'string',
      minWidth: 100,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params: GridRenderCellParams<BroadcastAccountItem>) => {
        const account = params?.row;
        if (!account) return null;
        return <ConnectionActionsCell account={account} />;
      },
    },
  ];

  return coldefs;
};

type GetBroadcastMessageColDefsArgs = {
  t: TFunction;
  timeZone?: string;
};

export const getBroadcastMessageColDefs = ({
  t,
  timeZone,
}: GetBroadcastMessageColDefsArgs): BroadcastMessageColDef[] => {
  const coldefs: BroadcastMessageColDef[] = [
    {
      field: 'title',
      headerName: t('blox.notifier.search.title'),
      sortable: true,
      resizable: true,
      flex: 2,
      display: 'flex',
      type: 'string',
      minWidth: 150,
      hideable: false,
      renderCell: (params: GridRenderCellParams<BroadcastMessageItem>) => {
        const broadcastMsg = params?.row;
        if (!broadcastMsg) {
          return null;
        }
        // see component notes for showMessage prop details
        return (
          <BroadcastMessageTitleCell
            messageItem={broadcastMsg}
            showMessage={false}
          />
        );
      },
    },
    {
      field: 'send_date',
      headerName: t('blox.notifier.search.send_date'),
      sortable: true,
      resizable: true,
      flex: 1,
      display: 'text',
      type: 'date',
      minWidth: 150,
      valueFormatter: (value, row) => {
        const startDate = row?.send_date;
        const result = hasValue(startDate)
          ? formatDate({
              date: startDate,
              shiftTz: timeZone,
              format: MMM_DD_YYYY_C_12HHMM,
            })
          : '';
        return result;
      },
    },
    {
      field: 'type',
      headerName: t('blox.notifier.search.type'),
      sortable: true,
      resizable: true,
      flex: 1,
      display: 'text',
      type: 'string',
      minWidth: 100,
    },
    {
      field: 'send_status',
      headerName: t('blox.notifier.search.send_status'),
      sortable: true,
      resizable: true,
      flex: 1,
      display: 'flex',
      type: 'string',
      minWidth: 100,
      renderCell: (params: GridRenderCellParams<BroadcastMessageItem>) => {
        const broadcastMsg = params?.row;
        const sendStatus = broadcastMsg?.send_status;
        if (!sendStatus) {
          return null;
        }
        return <NotifierStatusBadge messageItem={broadcastMsg} />;
      },
    },
    {
      field: 'channels',
      headerName: t('blox.notifier.search.channels'),
      sortable: false,
      resizable: true,
      flex: 1,
      display: 'flex',
      type: 'string',
      minWidth: 100,
      renderCell: (params: GridRenderCellParams<BroadcastMessageItem>) => {
        const broadcastMsg = params?.row;
        if (!broadcastMsg) {
          return null;
        }
        return <BroadcastMessageChannelsCell messageItem={broadcastMsg} />;
      },
    },
    {
      field: 'target',
      headerName: t('blox.notifier.search.target'),
      sortable: false,
      resizable: true,
      flex: 1,
      display: 'text',
      type: 'string',
      minWidth: 100,
    },
    {
      field: 'domain',
      headerName: t('blox.notifier.search.domain'),
      sortable: false,
      resizable: true,
      flex: 1,
      display: 'text',
      type: 'string',
      minWidth: 100,
    },
    {
      field: 'asset_app',
      headerName: t('blox.notifier.search.asset_app'),
      sortable: true,
      resizable: true,
      flex: 1,
      display: 'text',
      type: 'string',
      minWidth: 100,
    },
    {
      field: 'actions',
      type: 'actions' as const,
      width: 50,
      sortable: false,
      resizable: false,
      disableColumnMenu: true,
      hideable: false,
      renderCell: () => <SearchRowActionsCell />,
    },
  ];

  return coldefs;
};

type GetTemplatesColDefsARgs = {
  t: TFunction;
  timeZone?: string;
  view: SearchContextType['view'];
};

export const getTemplatesColDefs = ({
  t,
  timeZone,
  view,
}: GetTemplatesColDefsARgs): EditorialGridColDef[] => {
  const templateColDefs = [
    {
      field: 'title',
      headerName: t('blox.general.template'),
      renderCell: (params: GridRenderCellParams) =>
        view === 'list' ? (
          <TitleField record={params?.row} />
        ) : (
          <SummaryCard
            record={params?.row}
            view={view}
          />
        ),
      flex: 2,
      minWidth: view === 'list' ? 250 : 500,
      hideable: false,
    },
    {
      field: 'last_modified_by',
      headerName: t('blox.search.table.author', 'Author'),
      renderCell: (params: GridRenderCellParams) => (
        <AuthorsField
          record={params.row}
          lastModified={true}
        />
      ),
      flex: 0.8,
      minWidth: 120,
      sortable: false,
    },
    {
      field: 'start_time',
      headerName: t('blox.search.table.created', 'Created'),
      valueGetter: (value: any) => {
        if (!value) return '';
        const formattedDate = formatDate({
          date: value,
          shiftTz: timeZone,
          format: {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          },
        });
        return formattedDate;
      },
      flex: 0.6,
      minWidth: 120,
    },
    {
      field: 'actions',
      headerName: t('blox.search.table.actions', 'Actions'),
      width: 170,
      minWidth: 170,
      sortable: false,
      resizable: false,
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => (
        <TemplatesRowActions record={params?.row} />
      ),
    },
  ];

  return templateColDefs;
};
